/*
 * FUROMA scent picker — "pick N of M" chip UI for combination variants.
 *
 * Each variant is one combination (e.g. "Wagging Tails + Lap Nap + Forest Pawprints").
 * This element renders a chip per scent, and once N are picked it checks the matching
 * hidden radio and fires `change` — from there Dawn's VariantSelects / product-info
 * handle price, inventory, media and add-to-cart exactly as they do for any product.
 *
 * Matching is set-based, so the order scents are written in the Shopify option value
 * does not have to match the order they are clicked.
 */
if (!customElements.get('furoma-scent-picker')) {
  customElements.define(
    'furoma-scent-picker',
    class FuromaScentPicker extends HTMLElement {
      connectedCallback() {
        this.pickCount = parseInt(this.dataset.pickCount, 10) || 3;
        this.separator = this.dataset.separator || ' + ';
        /* Dawn restores the button label from window.variantStrings.addToCart after every
           variant change, so the set-specific label has to live there for this page. */
        this.addLabel = this.dataset.addLabel || '';
        if (this.addLabel && window.variantStrings) window.variantStrings.addToCart = this.addLabel;
        this.combos = this.parseCombos();
        this.selected = new Set(this.initialSelection());

        this.addEventListener('click', this.onRowClick);
        this.addEventListener('keydown', this.onRowKeydown);
        this.render();
      }

      disconnectedCallback() {
        clearTimeout(this.hintTimer);
      }

      parseCombos() {
        const el = this.querySelector('[data-fsp-combos]');
        if (!el) return [];

        let raw = [];
        try {
          raw = JSON.parse(el.textContent);
        } catch (error) {
          console.warn('[furoma-scent-picker] could not parse combo data', error);
          return [];
        }

        return raw.map((combo) => ({
          value: combo.value,
          available: combo.available,
          scents: this.splitCombo(combo.value),
          key: this.keyOf(this.splitCombo(combo.value)),
        }));
      }

      splitCombo(value) {
        return String(value)
          .split(this.separator.trim())
          .map((scent) => scent.trim())
          .filter(Boolean);
      }

      keyOf(scents) {
        return [...scents].sort().join('|');
      }

      /* Seed the chips from whichever radio Shopify rendered as checked. */
      initialSelection() {
        const checked = this.querySelector('[data-fsp-native] input[type="radio"]:checked');
        return checked ? this.splitCombo(checked.value) : [];
      }

      get rows() {
        return Array.from(this.querySelectorAll('[data-fsp-scent]'));
      }

      onRowClick = (event) => {
        const row = event.target.closest('[data-fsp-scent]');
        if (row) this.toggle(row);
      };

      /* role="checkbox" owes the user Space; buttons only give us Enter for free. */
      onRowKeydown = (event) => {
        if (event.key !== ' ' && event.key !== 'Spacebar') return;
        const row = event.target.closest('[data-fsp-scent]');
        if (!row) return;
        event.preventDefault();
        this.toggle(row);
      };

      toggle(row) {
        const scent = row.dataset.fspScent;

        if (this.selected.has(scent)) {
          this.selected.delete(scent);
        } else {
          if (row.dataset.soldout === 'true') return;
          // At the limit the dimmed chips look the same as sold-out ones, so say why
          // nothing happened rather than failing silently.
          if (this.selected.size >= this.pickCount) return this.flashSwapHint();
          this.selected.add(scent);
        }

        this.render();
        this.syncVariant();
      }

      flashSwapHint() {
        const hint = this.querySelector('[data-fsp-hint]');
        if (!hint) return;

        hint.textContent = 'Tap a selected scent to swap it out.';
        hint.hidden = false;
        clearTimeout(this.hintTimer);
        this.hintTimer = setTimeout(() => {
          if (this.selected.size === this.pickCount) hint.hidden = true;
        }, 2600);
      }

      /* A scent is pickable while at least one combination containing it is in stock. */
      isScentAvailable(scent) {
        const relevant = this.combos.filter((combo) => combo.scents.includes(scent));
        return relevant.length === 0 || relevant.some((combo) => combo.available);
      }

      render() {
        const complete = this.selected.size === this.pickCount;

        this.rows.forEach((row) => {
          const scent = row.dataset.fspScent;
          const isSelected = this.selected.has(scent);
          const soldOut = !this.isScentAvailable(scent);

          row.setAttribute('aria-checked', String(isSelected));
          row.dataset.soldout = String(soldOut);
          row.dataset.locked = String(!isSelected && !soldOut && this.selected.size >= this.pickCount);
          row.setAttribute('aria-disabled', String(soldOut));

          const soldOutTag = row.querySelector('[data-fsp-soldout]');
          if (soldOutTag) soldOutTag.hidden = !soldOut;
        });

        const counter = this.querySelector('[data-fsp-count]');
        if (counter) {
          counter.textContent = `${this.selected.size} of ${this.pickCount} selected`;
          counter.dataset.complete = String(complete);
        }

        const hint = this.querySelector('[data-fsp-hint]');
        if (hint) {
          const remaining = this.pickCount - this.selected.size;
          hint.hidden = complete;
          if (!complete) {
            hint.textContent =
              remaining > 0
                ? `Pick ${remaining} more scent${remaining === 1 ? '' : 's'} to add this set to your cart.`
                : '';
          }
        }

        this.toggleSubmit(complete);
      }

      /* Below the pick count there is no variant to add — hold the button until there is.
         Dawn re-enables it on the variant change we fire once the set is complete. */
      toggleSubmit(enabled) {
        const button = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        if (!button) return;
        const label = button.querySelector('span');
        const remaining = this.pickCount - this.selected.size;

        if (enabled) {
          if (button.dataset.fspHeld === 'true') {
            button.removeAttribute('disabled');
            delete button.dataset.fspHeld;
          }
          /* Complete set: Dawn rewrites this on the variant change that follows, using the
             same string, so setting it here only covers the page-load state. */
          if (label && this.addLabel && !button.hasAttribute('disabled')) label.textContent = this.addLabel;
        } else {
          button.setAttribute('disabled', 'disabled');
          button.dataset.fspHeld = 'true';
          /* SELECT 3 SCENTS → SELECT 2 MORE → SELECT 1 MORE (copy system button states). */
          if (label) {
            label.textContent =
              remaining === this.pickCount ? `Select ${this.pickCount} scents` : `Select ${remaining} more`;
          }
        }
      }

      syncVariant() {
        if (this.selected.size !== this.pickCount) return;

        const key = this.keyOf([...this.selected]);
        const match = this.combos.find((combo) => combo.key === key);
        if (!match) return;

        const radio = this.querySelector(
          `[data-fsp-native] input[type="radio"][value="${CSS.escape(match.value)}"]`
        );
        if (!radio || radio.checked) return;

        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}
